import React, { useState, useEffect } from 'react';
import './ProductionPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

const ProductionPage = () => {
    const [machines, setMachines] = useState([]);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [qeJobs, setQeJobs] = useState([]);
    const [activeMeasurements, setActiveMeasurements] = useState([]);
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [showAddJobModal, setShowAddJobModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [searchWT, setSearchWT] = useState('');
    const [showAllMachines, setShowAllMachines] = useState(false);

    // Fetch machines and operators on mount
    useEffect(() => {
        fetchMachines();
        fetchOperators();
    }, []);

    // Fetch active measurements when machine is selected
    useEffect(() => {
        if (selectedMachine) {
            fetchActiveMeasurements();
        }
    }, [selectedMachine]);

    const fetchMachines = async () => {
        try {
            const response = await fetch(`${API_URL}/production/machines`);
            if (!response.ok) throw new Error('Failed to fetch machines');
            const data = await response.json();
            setMachines(data);
        } catch (err) {
            console.error('Error fetching machines:', err);
            setError('Failed to load machines');
        }
    };

    const fetchOperators = async () => {
        try {
            const response = await fetch(`${API_URL}/operators?activeOnly=true`);
            if (!response.ok) throw new Error('Failed to fetch operators');
            const data = await response.json();
            setOperators(data);
        } catch (err) {
            console.error('Error fetching operators:', err);
        }
    };

    const searchJobByWT = async (wtNumber) => {
        if (!wtNumber || wtNumber.trim() === '') {
            setError('Please enter a WT number');
            return;
        }
        
        setLoading(true);
        setError(null);
        setQeJobs([]);
        
        try {
            const response = await fetch(`${API_URL}/production/search-job?wtNumber=${encodeURIComponent(wtNumber.trim())}`);
            
            if (response.status === 404) {
                setError(`Job ${wtNumber} not found`);
                setQeJobs([]);
                return;
            }
            
            if (!response.ok) throw new Error('Failed to search for job');
            
            const data = await response.json();
            setQeJobs([data]); // Wrap in array for consistency
            setSearchWT(''); // Clear search box after successful search
        } catch (err) {
            console.error('Error searching for job:', err);
            setError('Failed to search for job. Please try again.');
            setQeJobs([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            searchJobByWT(searchWT);
        }
    };

    const fetchActiveMeasurements = async () => {
        try {
            const response = await fetch(`${API_URL}/production/measurements/active`);
            if (!response.ok) throw new Error('Failed to fetch active measurements');
            const data = await response.json();
            
            // Filter by selected machine (using actualMachineId)
            const filtered = selectedMachine 
                ? data.filter(m => m.actualMachineId === selectedMachine.RESOURCEID)
                : data;
            
            setActiveMeasurements(filtered);
        } catch (err) {
            console.error('Error fetching active measurements:', err);
        }
    };

    const handleMachineSelect = (machine) => {
        setSelectedMachine(machine);
        setSearchWT('');
        setQeJobs([]);
        setError(null);
    };

    // Removed handleShowAllMachines - no longer needed with search-based approach

    const handleSearchChange = (value) => {
        setSearchWT(value);
    };

    const getFilteredJobs = () => {
        if (!searchWT.trim()) return qeJobs;
        return qeJobs.filter(job => 
            job.WT_NUMBER.toLowerCase().includes(searchWT.toLowerCase())
        );
    };

    const isJobActive = (transId) => {
        return activeMeasurements.some(m => m.qeTransId === transId);
    };

    const handleRefresh = async () => {
        if (!selectedMachine) return;
        setRefreshing(true);
        await fetchActiveMeasurements();
        setRefreshing(false);
    };

    const handleAddJobClick = (job) => {
        // Check if this job is already active
        const existingMeasurement = activeMeasurements.find(m => m.qeTransId === job.TRANSID);
        
        if (existingMeasurement) {
            // Scroll to the active jobs section
            const activeSection = document.querySelector('.active-jobs-section');
            if (activeSection) {
                activeSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                // Highlight the specific card briefly
                setTimeout(() => {
                    const card = document.querySelector(`[data-measurement-id="${existingMeasurement.id}"]`);
                    if (card) {
                        card.classList.add('highlight-flash');
                        setTimeout(() => card.classList.remove('highlight-flash'), 2000);
                    }
                }, 500);
            }
            return;
        }
        
        setSelectedJob(job);
        setShowAddJobModal(true);
    };

    const handleAddJob = async (job, actualMachineId, actualMachineName, operatorId) => {
        try {
            const response = await fetch(`${API_URL}/production/measurements`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    qeTransId: job.TRANSID,
                    actualMachineId,
                    actualMachineName,
                    operatorId
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to add job');
            }

            // Refresh lists
            await fetchActiveMeasurements();
            setShowAddJobModal(false);
            setSelectedJob(null);
            setQeJobs([]); // Clear search results
        } catch (err) {
            console.error('Error adding job:', err);
            alert(err.message);
        }
    };

    const handleUpdateMeasurement = async () => {
        // Just refresh the measurements list
        await fetchActiveMeasurements();
    };

    return (
        <div className="production-page">
            <div className="production-header">
                <h1>Production Measurement</h1>
                {selectedMachine && (
                    <button 
                        className="refresh-button" 
                        onClick={handleRefresh}
                        disabled={refreshing}
                    >
                        {refreshing ? 'Refreshing...' : '🔄 Refresh'}
                    </button>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* Machine Selection */}
            {!selectedMachine && (
                <div className="machine-selection">
                    <h2>Select Machine</h2>
                    <div className="machine-grid">
                        {machines.map(machine => (
                            <button
                                key={machine.RESOURCEID}
                                className="machine-card"
                                onClick={() => handleMachineSelect(machine)}
                            >
                                <div className="machine-name">{machine.DESCRIPTION}</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Active Jobs View */}
            {selectedMachine && (
                <div className="active-jobs-view">
                    <div className="view-header">
                        <h2>{selectedMachine.DESCRIPTION}</h2>
                        <button 
                            className="back-button"
                            onClick={() => setSelectedMachine(null)}
                        >
                            ← Back to Machines
                        </button>
                    </div>

                    {/* Active Jobs Section - Collapsible */}
                    {activeMeasurements.length > 0 && (
                        <div className="active-jobs-section">
                            <div className="section-header">
                                <h3>Active Jobs ({activeMeasurements.length})</h3>
                                <span className="section-hint">Jobs currently being tracked</span>
                            </div>
                            <div className="measurements-list">
                                {activeMeasurements.map(measurement => (
                                    <MeasurementCard
                                        key={measurement.id}
                                        measurement={measurement}
                                        onUpdate={handleUpdateMeasurement}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Search and Filter Controls */}
                    <div className="search-controls">
                        <div className="search-box">
                            <input
                                type="text"
                                placeholder="🔍 Enter WT Number (e.g., 66310) and press Enter or click Search"
                                value={searchWT}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onKeyPress={handleSearchKeyPress}
                                className="search-input"
                            />
                            <button 
                                className="search-button"
                                onClick={() => searchJobByWT(searchWT)}
                                disabled={!searchWT.trim()}
                            >
                                Search
                            </button>
                            {searchWT && (
                                <button 
                                    className="clear-search"
                                    onClick={() => {
                                        setSearchWT('');
                                        setQeJobs([]);
                                        setError(null);
                                    }}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Available QE Jobs */}
                    <div className="qe-jobs-section">
                        <h3>Available Jobs - {selectedMachine.DESCRIPTION}</h3>
                        {loading ? (
                            <div className="loading">Searching for job...</div>
                        ) : error ? (
                            <div className="error-message">{error}</div>
                        ) : qeJobs.length === 0 ? (
                            <div className="no-data">
                                Enter a WT number above to search for a job
                            </div>
                        ) : (
                            <div className="qe-jobs-list">
                                {getFilteredJobs().map(job => (
                                    <div key={job.TRANSID} className="qe-job-card">
                                        <div className="job-info">
                                            <div className="job-wt">{job.WT_NUMBER}</div>
                                            <div className="job-customer">
                                                {job.CUSTOMER_NAME || `${job.FIRSTNAME || ''} ${job.SURNAME || ''}`.trim() || 'No customer'}
                                            </div>
                                            <div className="job-description">{job.JOB_DESCRIPTION}</div>
                                            <div className="job-meta">
                                                <span className="job-estimated">Est: {job.ESTIMATED_METERS}m</span>
                                                {showAllMachines && (
                                                    <span className="job-machine">📍 {job.MACHINE_NAME}</span>
                                                )}
                                            </div>
                                        </div>
                                        {isJobActive(job.TRANSID) ? (
                                            <button
                                                className="continue-job-button"
                                                onClick={() => handleAddJobClick(job)}
                                            >
                                                📝 Part Printed
                                            </button>
                                        ) : (
                                            <button
                                                className="add-job-button"
                                                onClick={() => handleAddJobClick(job)}
                                            >
                                                📊 Log Production Details
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Add Job Modal */}
            {showAddJobModal && selectedJob && (
                <AddJobModal
                    job={selectedJob}
                    machines={machines}
                    operators={operators}
                    defaultMachine={selectedMachine}
                    onAdd={handleAddJob}
                    onCancel={() => {
                        setShowAddJobModal(false);
                        setSelectedJob(null);
                    }}
                />
            )}
        </div>
    );
};

// Add Job Modal Component
const AddJobModal = ({ job, machines, operators, defaultMachine, onAdd, onCancel }) => {
    const [selectedOperatorId, setSelectedOperatorId] = useState('');

    const handleSubmit = () => {
        onAdd(
            job,
            defaultMachine.RESOURCEID,
            defaultMachine.DESCRIPTION,
            selectedOperatorId || null
        );
    };

    return (
        <div className="modal-overlay" onClick={onCancel}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Add Job to Production</h2>
                <div className="modal-job-info">
                    <div><strong>WT:</strong> {job.WT_NUMBER}</div>
                    <div><strong>Customer:</strong> {job.CUSTOMER_NAME || 'N/A'}</div>
                    <div><strong>Description:</strong> {job.JOB_DESCRIPTION}</div>
                    <div><strong>Estimated:</strong> {job.ESTIMATED_METERS}m</div>
                    <div><strong>Quoted Machine:</strong> {job.MACHINE_NAME}</div>
                </div>

                <div className="modal-form">
                    <div className="form-group">
                        <label>Operator:</label>
                        <select 
                            value={selectedOperatorId}
                            onChange={(e) => setSelectedOperatorId(e.target.value)}
                        >
                            <option value="">Select Operator (Optional)</option>
                            {operators.map(operator => (
                                <option key={operator.id} value={operator.id}>
                                    {operator.name} {operator.employeeNumber ? `(${operator.employeeNumber})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="modal-save-button" onClick={handleSubmit}>
                        Continue
                    </button>
                    <button className="modal-cancel-button" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Measurement Card Component
const MeasurementCard = ({ measurement, onUpdate }) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';
    
    // New run inputs
    const [newStartingMeters, setNewStartingMeters] = useState('');
    const [newEndingMeters, setNewEndingMeters] = useState('');
    
    // Material specs
    const [actualWidth, setActualWidth] = useState(measurement.actualWidth || '');
    const [actualAcross, setActualAcross] = useState(measurement.actualAcross || '');
    const [isCompleted, setIsCompleted] = useState(measurement.isCompleted || false);
    const [isEditing, setIsEditing] = useState(false);
    // Start collapsed if there are existing runs, expanded if brand new
    const [isExpanded, setIsExpanded] = useState(!measurement.runs || measurement.runs.length === 0);

    // Initialize starting meters as empty (operator will enter from machine odometer)
    useEffect(() => {
        setNewStartingMeters('');
    }, [measurement.runs]);

    const handleSaveRun = async () => {
        try {
            // Validate inputs
            if (!newStartingMeters || !newEndingMeters) {
                alert('Please enter both starting and ending meters');
                return;
            }

            const start = parseFloat(newStartingMeters);
            const end = parseFloat(newEndingMeters);

            if (isNaN(start) || isNaN(end)) {
                alert('Please enter valid numbers for meters');
                return;
            }

            if (end <= start) {
                alert('Ending meters must be greater than starting meters');
                return;
            }

            // Create new run
            const response = await fetch(`${API_URL}/production/measurements/${measurement.id}/runs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    startingMeters: start,
                    endingMeters: end
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to save run');
            }

            // Update material specs and completion if needed
            if (actualWidth || actualAcross || isCompleted) {
                const updateResponse = await fetch(`${API_URL}/production/measurements/${measurement.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        actualWidth: actualWidth ? parseFloat(actualWidth) : null,
                        actualAcross: actualAcross ? parseInt(actualAcross) : null,
                        isCompleted
                    })
                });

                if (!updateResponse.ok) {
                    const errorData = await updateResponse.json();
                    console.error('Error updating measurement:', errorData);
                }
            }

            // Refresh data
            onUpdate();
            setIsEditing(false);
            setIsExpanded(false);
            setNewEndingMeters('');
        } catch (error) {
            console.error('Error saving run:', error);
            alert(`Failed to save run: ${error.message}`);
        }
    };

    const totalActualMeters = measurement.totalActualMeters 
        ? parseFloat(measurement.totalActualMeters).toFixed(1)
        : '0.0';
    
    const estimatedMeters = parseFloat(measurement.estimatedMeters).toFixed(1);
    const progressPercent = measurement.totalActualMeters 
        ? Math.round((parseFloat(measurement.totalActualMeters) / parseFloat(measurement.estimatedMeters)) * 100)
        : 0;

    return (
        <div className="measurement-card" data-measurement-id={measurement.id}>
            <div className="measurement-header">
                <div className="measurement-wt">{measurement.wtNumber}</div>
                <div className="measurement-customer">{measurement.customerName || 'No customer'}</div>
            </div>
            <div className="measurement-description">{measurement.wtDescription}</div>
            <div className="measurement-info">
                <div>Estimated: {estimatedMeters}m</div>
                <div>Actual: {totalActualMeters}m {measurement.runs && measurement.runs.length > 0 && `(${measurement.runs.length} run${measurement.runs.length > 1 ? 's' : ''})`}</div>
                <div>Progress: {progressPercent}%</div>
                {measurement.machineChanged && (
                    <div className="machine-changed-badge">⚠️ Machine Changed</div>
                )}
                {measurement.operator && (
                    <div className="operator-info">👤 {measurement.operator.name}</div>
                )}
            </div>

            {/* Progress Bar */}
            <div className="progress-bar-container">
                <div className="progress-bar" style={{ width: `${Math.min(progressPercent, 100)}%` }}></div>
            </div>

            {!isExpanded && (
                <button 
                    className="expand-button"
                    onClick={() => setIsExpanded(true)}
                >
                    📝 Continue Editing
                </button>
            )}
            
            {isExpanded && (
                <div className="measurement-inputs">
                {/* Previous Runs */}
                {measurement.runs && measurement.runs.length > 0 && (
                    <div className="previous-runs">
                        <h4>Previous Runs:</h4>
                        {measurement.runs.map((run) => (
                            <div key={run.id} className="run-item">
                                <span className="run-number">Run #{run.runNumber}:</span>
                                <span className="run-meters">{parseFloat(run.startingMeters).toFixed(1)} → {parseFloat(run.endingMeters).toFixed(1)} = {parseFloat(run.actualMeters).toFixed(1)}m</span>
                                <span className="run-check">✓</span>
                            </div>
                        ))}
                        <div className="total-so-far">
                            <strong>Total so far: {totalActualMeters}m</strong>
                        </div>
                    </div>
                )}

                {/* New Run */}
                <div className="new-run-section">
                    <h4>New Run #{(measurement.runs?.length || 0) + 1}:</h4>
                    <div className="input-row">
                        <div className="input-group">
                            <label>Starting Meters:</label>
                            <input
                                type="number"
                                step="0.1"
                                value={newStartingMeters}
                                onChange={(e) => {
                                    setNewStartingMeters(e.target.value);
                                    setIsEditing(true);
                                }}
                                placeholder="0.0"
                            />
                        </div>
                        <div className="input-group">
                            <label>Ending Meters:</label>
                            <input
                                type="number"
                                step="0.1"
                                value={newEndingMeters}
                                onChange={(e) => {
                                    setNewEndingMeters(e.target.value);
                                    setIsEditing(true);
                                }}
                                placeholder="0.0"
                            />
                        </div>
                    </div>
                </div>
                <div className="input-row">
                    <div className="input-group">
                        <label>Material Width (mm):</label>
                        <input
                            type="number"
                            step="0.1"
                            value={actualWidth}
                            onChange={(e) => {
                                setActualWidth(e.target.value);
                                setIsEditing(true);
                            }}
                            placeholder="Width"
                        />
                    </div>
                    <div className="input-group">
                        <label>Qty Across:</label>
                        <input
                            type="number"
                            step="1"
                            value={actualAcross}
                            onChange={(e) => {
                                setActualAcross(e.target.value);
                                setIsEditing(true);
                            }}
                            placeholder="Across"
                        />
                    </div>
                </div>
                <div className="completion-group">
                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={isCompleted}
                            onChange={(e) => {
                                setIsCompleted(e.target.checked);
                                setIsEditing(true);
                            }}
                        />
                        Mark as Complete
                    </label>
                </div>

                {isEditing && newEndingMeters && (
                    <button className="save-button" onClick={handleSaveRun}>
                        {isCompleted ? 'Complete Job' : 'Save Progress'}
                    </button>
                )}
            </div>
            )}
        </div>
    );
};

export default ProductionPage;
